// Without an alliance (spec §3.6): invitations, founding, and the alliance list with applying.
import type { AllianceSummary } from '@stars-and-ships/api-types';
import { useMutation } from '@tanstack/react-query';
import { Search } from 'lucide-react';
import { type FormEvent, useState } from 'react';
import { Link } from 'react-router';
import { endpoints } from '../../api/endpoints';
import { useAccount, useAllianceSearch, useMyAllianceInvites } from '../../api/queries';
import { formatDuration } from '../../labels';
import { useNow } from '../../state/live';
import { Button, ErrorNotice, inputFrame, Notice, Panel, Spinner, TextField } from '../ui';
import { ApplyForm } from './ApplyForm';
import { Emblem } from './Emblem';
import { useRefresh } from './shared';

export function NoAllianceView({
  galaxyId,
  rejoinAllowedAt,
}: {
  galaxyId: string;
  rejoinAllowedAt: string | null;
}) {
  const now = useNow();
  const cooldownMs = rejoinAllowedAt ? Date.parse(rejoinAllowedAt) - now : 0;
  return (
    <>
      <h1 className="font-display text-xl font-bold uppercase tracking-[0.08em] text-text">
        Alliance
      </h1>
      <p className="text-sm text-muted">
        Alliance members share their Warp Gates, reinforce each other’s systems and, from Commander
        rank, see each other’s intel.
      </p>
      {cooldownMs > 0 ? (
        <Notice>You can join or found an alliance again in {formatDuration(cooldownMs)}.</Notice>
      ) : null}
      <div className="grid gap-4 lg:grid-cols-2 lg:items-start">
        <div className="space-y-4">
          <MyInvites galaxyId={galaxyId} />
          <CreateAlliance galaxyId={galaxyId} />
        </div>
        <AllianceList galaxyId={galaxyId} />
      </div>
    </>
  );
}

/** The viewer's invitations and applications; `allianceId` limits them to one alliance (profile). */
export function MyInvites({ galaxyId, allianceId }: { galaxyId: string; allianceId?: string }) {
  const invites = useMyAllianceInvites(galaxyId, true);
  const refresh = useRefresh(galaxyId);
  const [error, setError] = useState<unknown>(null);
  const answer = useMutation({
    mutationFn: ({ id, accept }: { id: string; accept: boolean }) =>
      accept
        ? endpoints.acceptInvite(galaxyId, id).then(() => undefined)
        : endpoints.declineInvite(galaxyId, id),
    onSuccess: () => {
      setError(null);
      refresh();
    },
    onError: setError,
  });
  const items = (invites.data?.items ?? []).filter(
    (invite) => !allianceId || invite.alliance.id === allianceId
  );
  if (items.length === 0) return null;
  return (
    <Panel title="Invitations and applications">
      {error ? <ErrorNotice error={error} /> : null}
      <ul className="space-y-2">
        {items.map((invite) => (
          <li
            key={invite.id}
            data-my-invite={invite.alliance.tag}
            className="flex flex-wrap items-center justify-between gap-2 hud-cut-sm border border-line bg-void/60 p-3"
          >
            <div className="min-w-0 text-sm">
              <p className="text-text">
                {invite.alliance.name} <span className="text-info">[{invite.alliance.tag}]</span>
              </p>
              <p className="text-xs text-muted">
                {invite.kind === 'invite'
                  ? `Invited by ${invite.invitedBy?.displayName ?? 'an officer'}`
                  : 'Your application is waiting for an officer'}
              </p>
            </div>
            <div className="flex gap-2">
              {invite.kind === 'invite' ? (
                <Button
                  disabled={answer.isPending}
                  onClick={() => answer.mutate({ id: invite.id, accept: true })}
                >
                  Join
                </Button>
              ) : null}
              <Button
                variant="secondary"
                disabled={answer.isPending}
                onClick={() => answer.mutate({ id: invite.id, accept: false })}
              >
                {invite.kind === 'invite' ? 'Decline' : 'Withdraw'}
              </Button>
            </div>
          </li>
        ))}
      </ul>
    </Panel>
  );
}

function CreateAlliance({ galaxyId }: { galaxyId: string }) {
  const account = useAccount();
  const refresh = useRefresh(galaxyId);
  const [name, setName] = useState('');
  const [tag, setTag] = useState('');
  const [description, setDescription] = useState('');
  const create = useMutation({
    mutationFn: () =>
      endpoints.createAlliance(galaxyId, {
        name: name.trim(),
        tag,
        description: description.trim(),
      }),
    onSuccess: refresh,
  });
  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    create.mutate();
  };
  return (
    <Panel title="Found an alliance">
      {account.data && !account.data.account.phoneVerified ? (
        <Notice>
          Founding an alliance needs a verified phone number. In the test build the test team
          verifies it for you — joining an alliance works without it.
        </Notice>
      ) : (
        <form data-create-alliance className="space-y-3" onSubmit={onSubmit}>
          <TextField
            label="Name"
            value={name}
            minLength={3}
            maxLength={30}
            required
            onChange={(e) => setName(e.target.value)}
          />
          <TextField
            label="Tag"
            hint="2–5 capital letters or digits, shown as [TAG] next to member names."
            value={tag}
            pattern="[A-Z0-9]{2,5}"
            required
            onChange={(e) =>
              setTag(
                e.target.value
                  .toUpperCase()
                  .replace(/[^A-Z0-9]/g, '')
                  .slice(0, 5)
              )
            }
          />
          <TextField
            label="Description (optional)"
            value={description}
            maxLength={500}
            onChange={(e) => setDescription(e.target.value)}
          />
          {create.error ? <ErrorNotice error={create.error} /> : null}
          <Button type="submit" disabled={create.isPending}>
            Found alliance
          </Button>
        </form>
      )}
    </Panel>
  );
}

function AllianceList({ galaxyId }: { galaxyId: string }) {
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const list = useAllianceSearch(galaxyId, query, true);
  return (
    <Panel title="Alliances">
      <form
        className="mb-3 flex gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          setQuery(search.trim());
        }}
      >
        <input
          aria-label="Search alliances"
          placeholder="Name or tag"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className={`${inputFrame} min-w-0 flex-1`}
        />
        <Button type="submit" variant="secondary" aria-label="Search">
          <Search className="size-4" aria-hidden />
        </Button>
      </form>
      {list.isPending ? <Spinner /> : null}
      {list.error ? <ErrorNotice error={list.error} /> : null}
      {list.data && list.data.items.length === 0 ? (
        <p className="text-sm text-muted">No alliances found.</p>
      ) : null}
      <ul className="space-y-2">
        {list.data?.items.map((alliance) => (
          <AllianceListItem key={alliance.id} galaxyId={galaxyId} alliance={alliance} />
        ))}
      </ul>
    </Panel>
  );
}

function AllianceListItem({ galaxyId, alliance }: { galaxyId: string; alliance: AllianceSummary }) {
  const [open, setOpen] = useState(false);
  return (
    <li data-alliance={alliance.tag} className="hud-cut-sm border border-line bg-void/60 p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex min-w-0 items-start gap-3">
          <Emblem emblem={alliance.emblem} tag={alliance.tag} size="sm" />
          <div className="min-w-0">
            <Link
              to={`/g/${galaxyId}/alliances/${alliance.id}`}
              className="text-sm text-text underline decoration-dotted underline-offset-2 hover:text-info"
            >
              {alliance.name} <span className="text-info">[{alliance.tag}]</span>
            </Link>
            <p className="text-xs text-muted">
              {alliance.memberCount}/{alliance.maxMembers} members ·{' '}
              {alliance.openRecruitment ? 'open for applications' : 'invitation only'}
            </p>
            {alliance.description ? (
              <p className="mt-1 text-xs text-muted">{alliance.description}</p>
            ) : null}
          </div>
        </div>
        {alliance.openRecruitment && !open ? (
          <Button variant="secondary" onClick={() => setOpen(true)}>
            Apply
          </Button>
        ) : null}
      </div>
      {open ? (
        <ApplyForm galaxyId={galaxyId} allianceId={alliance.id} onDone={() => setOpen(false)} />
      ) : null}
    </li>
  );
}
